import { useState, useEffect, useCallback } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { resolveLinkProps, isColorDark } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';

export default function BannerSliderSection({ block = {}, primaryColor, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const slides = content.slides || [];
    const [current, setCurrent] = useState(0);
    const [lbIndex, setLbIndex] = useState(-1);
    const autoplay = config.autoplay !== false;
    const interval = (config.interval || 5) * 1000;
    const showArrows = config.show_arrows !== false;
    const showDots = config.show_dots !== false;
    const variant = config.variant || 'default';

    const next = useCallback(() => setCurrent(c => (c + 1) % slides.length), [slides.length]);
    const prev = useCallback(() => setCurrent(c => (c - 1 + slides.length) % slides.length), [slides.length]);

    useEffect(() => {
        if (!autoplay || slides.length <= 1) return;
        const t = setInterval(next, interval);
        return () => clearInterval(t);
    }, [autoplay, interval, next, slides.length]);

    if (slides.length === 0) return (
        <section className="relative w-full aspect-[21/9] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <div className="text-center text-gray-400">
                <svg className="w-14 h-14 mx-auto mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="2" y="2" width="20" height="20" rx="2" strokeWidth={1.5}/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2 15l5-5 3 3 4-4 8 8"/></svg>
                <p className="text-sm font-medium">Agrega slides al banner</p>
            </div>
        </section>
    );

    // Resolve image URL for a given slide
    const resolveImage = (sl) => {
        return sl.image_folder_id && mediaFolders[sl.image_folder_id]
            ? (mediaFolders[sl.image_folder_id].find(f => String(f.id) === String(sl.image_file_id))?.image_url || sl.image_url)
            : sl.image_url;
    };

    const slide = slides[current];
    const bgImage = resolveImage(slide);

    const titleSrc = config.title_source || 'slide';
    const mainTitle = content.title || '';
    const mainSubtitle = content.subtitle || '';
    const displayTitle = titleSrc === 'main' ? mainTitle
        : titleSrc === 'auto' ? (slide.title || mainTitle)
        : slide.title; // slide (default)
    const displaySubtitle = titleSrc === 'main' ? mainSubtitle
        : titleSrc === 'auto' ? (slide.subtitle || mainSubtitle)
        : slide.subtitle;

    // Shared CTA button renderer
    const CtaButton = ({ sl, animStyle }) => {
        if (!sl.cta_text) return null;
        const btnStyle = sl.style || 'solid';
        const icon = sl.icon && getButtonIcon(resolveIconKey(sl.icon));
        const lp = resolveLinkProps(sl.cta_link || '#');
        const customBg = sl.bg_color;
        const customText = sl.text_color;
        let cls = 'mt-8 px-8 py-4 font-bold text-sm transition-all hover:scale-105 hover:shadow-2xl inline-flex items-center gap-2 ';
        let style = {};
        cls += btnStyle === 'pill' ? 'rounded-full ' : 'rounded-lg ';
        if (btnStyle === 'solid' || btnStyle === 'pill') {
            if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#ffffff' : '#111827'); }
            else { style.backgroundColor = primaryColor; style.color = '#ffffff'; }
        } else if (btnStyle === 'outline') {
            if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
            else { style.color = '#ffffff'; style.borderColor = '#ffffff'; cls += 'border-2 hover:bg-white/10'; }
        } else { style.color = customText || '#ffffff'; cls += 'hover:bg-white/10'; }
        if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
        return (
            <div style={animStyle}>
                <a {...lp} className={cls} style={style}>{icon}{sl.cta_text}</a>
            </div>
        );
    };

    // Shared arrows
    const Arrows = () => showArrows && slides.length > 1 ? (
        <>
            <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
        </>
    ) : null;

    // Shared dots
    const Dots = () => showDots && slides.length > 1 ? (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)}
                    className={`w-3 h-3 rounded-full transition-all ${i === current ? 'bg-white scale-110' : 'bg-white/40 hover:bg-white/60'}`} />
            ))}
        </div>
    ) : null;

    // Shared lightbox
    const LightboxEl = () => (
        <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
            slides={slides.map(sl => {
                const url = resolveImage(sl);
                return { src: url || '', alt: sl.title || '' };
            }).filter(sl => sl.src)} />
    );

    // ── Fade variant ──
    if (variant === 'fade') {
        return (
            <section className="relative overflow-hidden" style={{ minHeight: '500px', backgroundColor: slide.bg_color || primaryColor }}>
                {/* All slides stacked, only current visible via opacity */}
                {slides.map((sl, i) => {
                    const img = resolveImage(sl);
                    return (
                        <div key={i} className="absolute inset-0 transition-opacity duration-1000 ease-in-out" style={{ opacity: i === current ? 1 : 0 }}>
                            <div className="absolute inset-0" style={{ backgroundColor: sl.bg_color || primaryColor }}>
                                {img && <img src={img} alt="" className="w-full h-full object-cover" loading={i === 0 ? 'eager' : 'lazy'} onClick={() => setLbIndex(i)} style={{ cursor: 'pointer' }} />}
                                {img && <div className="absolute inset-0 bg-black/40" />}
                            </div>
                        </div>
                    );
                })}

                {/* Content */}
                <div className="relative z-10 flex items-center justify-center min-h-[500px] px-4 sm:px-6 lg:px-8 py-20">
                    <div className="max-w-3xl mx-auto text-center">
                        {displayTitle && <h2 key={`t-${current}`} className="text-3xl md:text-5xl font-bold text-white leading-tight transition-opacity duration-700" style={{ animation: 'bsFadeIn 0.7s ease-out' }}>
                            {displayTitle}
                        </h2>}
                        {displaySubtitle && (
                            <p key={`s-${current}`} className="text-lg md:text-xl text-white/80 mt-4 transition-opacity duration-700" style={{ animation: 'bsFadeIn 0.7s 0.1s ease-out both' }}>
                                {displaySubtitle}
                            </p>
                        )}
                        <CtaButton sl={slide} animStyle={{ animation: 'bsFadeIn 0.7s 0.2s ease-out both' }} />
                    </div>
                </div>

                <Arrows />
                <Dots />
                <LightboxEl />
                <style>{`
                    @keyframes bsFadeIn { from { opacity: 0; } to { opacity: 1; } }
                `}</style>
            </section>
        );
    }

    // ── Split-content variant ──
    if (variant === 'split-content') {
        return (
            <section className="relative overflow-hidden" style={{ minHeight: '500px', backgroundColor: slide.bg_color || primaryColor }}>
                <div className="flex flex-col md:flex-row min-h-[500px]">
                    {/* Left: image */}
                    <div className="relative w-full md:w-1/2 min-h-[280px] md:min-h-[500px]">
                        {slides.map((sl, i) => {
                            const img = resolveImage(sl);
                            return (
                                <div key={i} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: i === current ? 1 : 0 }}>
                                    {img ? (
                                        <img src={img} alt="" className="w-full h-full object-cover cursor-pointer" loading={i === 0 ? 'eager' : 'lazy'} onClick={() => setLbIndex(i)} />
                                    ) : (
                                        <div className="w-full h-full" style={{ backgroundColor: sl.bg_color || primaryColor }} />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Right: text content */}
                    <div className="w-full md:w-1/2 flex items-center justify-center px-6 sm:px-10 lg:px-16 py-12 md:py-20 relative" style={{ backgroundColor: slide.bg_color || primaryColor }}>
                        {bgImage && <div className="absolute inset-0 bg-black/60" />}
                        <div className="relative z-10 max-w-lg">
                            {displayTitle && <h2 key={`t-${current}`} className="text-3xl md:text-4xl font-bold text-white leading-tight" style={{ animation: 'bsSplitIn 0.6s ease-out' }}>
                                {displayTitle}
                            </h2>}
                            {displaySubtitle && (
                                <p key={`s-${current}`} className="text-lg text-white/80 mt-4" style={{ animation: 'bsSplitIn 0.6s 0.1s ease-out both' }}>
                                    {displaySubtitle}
                                </p>
                            )}
                            <CtaButton sl={slide} animStyle={{ animation: 'bsSplitIn 0.6s 0.2s ease-out both' }} />
                        </div>
                    </div>
                </div>

                <Arrows />
                <Dots />
                <LightboxEl />
                <style>{`
                    @keyframes bsSplitIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
                `}</style>
            </section>
        );
    }

    // ── Ken-burns variant ──
    if (variant === 'ken-burns') {
        const kbId = `kb-${block.id || 'bs'}`;
        return (
            <section className="relative overflow-hidden" style={{ minHeight: '500px', backgroundColor: slide.bg_color || primaryColor }}>
                {/* Background with ken-burns animation */}
                <div className="absolute inset-0 transition-all duration-700" style={{ backgroundColor: slide.bg_color || primaryColor }}>
                    {bgImage && <img key={`kb-img-${current}`} src={bgImage} alt="" className={`w-full h-full object-cover cursor-pointer ${kbId}-anim`} loading="eager" onClick={() => setLbIndex(current)} />}
                    {bgImage && <div className="absolute inset-0 bg-black/40" />}
                </div>

                {/* Content */}
                <div className="relative z-10 flex items-center justify-center min-h-[500px] px-4 sm:px-6 lg:px-8 py-20">
                    <div className="max-w-3xl mx-auto text-center">
                        {displayTitle && <h2 key={`t-${current}`} className="text-3xl md:text-5xl font-bold text-white leading-tight" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
                            {displayTitle}
                        </h2>}
                        {displaySubtitle && (
                            <p key={`s-${current}`} className="text-lg md:text-xl text-white/80 mt-4" style={{ animation: 'fadeInUp 0.6s 0.15s ease-out both' }}>
                                {displaySubtitle}
                            </p>
                        )}
                        <CtaButton sl={slide} animStyle={{ animation: 'fadeInUp 0.6s 0.3s ease-out both' }} />
                    </div>
                </div>

                <Arrows />
                <Dots />
                <LightboxEl />
                <style>{`
                    @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes bsKenBurns {
                        0% { transform: scale(1) translate(0, 0); }
                        50% { transform: scale(1.08) translate(-1%, -1%); }
                        100% { transform: scale(1) translate(0, 0); }
                    }
                    .${kbId}-anim {
                        animation: bsKenBurns 8s ease-in-out infinite alternate;
                    }
                `}</style>
            </section>
        );
    }

    // ── Default variant ──
    return (
        <section className="relative overflow-hidden" style={{ minHeight: '500px', backgroundColor: slide.bg_color || primaryColor }}>
            {/* Background */}
            <div className="absolute inset-0 transition-all duration-700" style={{ backgroundColor: slide.bg_color || primaryColor }}>
                {bgImage && <img src={bgImage} alt="" className="w-full h-full object-cover cursor-pointer" loading="eager" onClick={() => setLbIndex(current)} />}
                {bgImage && <div className="absolute inset-0 bg-black/40" />}
            </div>

            {/* Content */}
            <div className="relative z-10 flex items-center justify-center min-h-[500px] px-4 sm:px-6 lg:px-8 py-20">
                <div className="max-w-3xl mx-auto text-center">
                    {displayTitle && <h2 key={`t-${current}`} className="text-3xl md:text-5xl font-bold text-white leading-tight" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
                        {displayTitle}
                    </h2>}
                    {displaySubtitle && (
                        <p key={`s-${current}`} className="text-lg md:text-xl text-white/80 mt-4" style={{ animation: 'fadeInUp 0.6s 0.15s ease-out both' }}>
                            {displaySubtitle}
                        </p>
                    )}
                    {slide.cta_text && (() => {
                        const btnStyle = slide.style || 'solid';
                        const icon = slide.icon && getButtonIcon(resolveIconKey(slide.icon));
                        const lp = resolveLinkProps(slide.cta_link || '#');
                        const customBg = slide.bg_color;
                        const customText = slide.text_color;
                        let cls = 'mt-8 px-8 py-4 font-bold text-sm transition-all hover:scale-105 hover:shadow-2xl inline-flex items-center gap-2 ';
                        let style = {};
                        cls += btnStyle === 'pill' ? 'rounded-full ' : 'rounded-lg ';
                        if (btnStyle === 'solid' || btnStyle === 'pill') {
                            if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#ffffff' : '#111827'); }
                            else { style.backgroundColor = primaryColor; style.color = '#ffffff'; }
                        } else if (btnStyle === 'outline') {
                            if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                            else { style.color = '#ffffff'; style.borderColor = '#ffffff'; cls += 'border-2 hover:bg-white/10'; }
                        } else { style.color = customText || '#ffffff'; cls += 'hover:bg-white/10'; }
                        if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                        return (
                            <div style={{ animation: 'fadeInUp 0.6s 0.3s ease-out both' }}>
                                <a {...lp} className={cls} style={style}>{icon}{slide.cta_text}</a>
                            </div>
                        );
                    })()}
                </div>
            </div>

            {/* Arrows */}
            {showArrows && slides.length > 1 && (
                <>
                    <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-all">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                </>
            )}

            {/* Dots */}
            {showDots && slides.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                    {slides.map((_, i) => (
                        <button key={i} onClick={() => setCurrent(i)}
                            className={`w-3 h-3 rounded-full transition-all ${i === current ? 'bg-white scale-110' : 'bg-white/40 hover:bg-white/60'}`} />
                    ))}
                </div>
            )}

            <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
                slides={slides.map(sl => {
                    const url = sl.image_folder_id && mediaFolders[sl.image_folder_id]
                        ? (mediaFolders[sl.image_folder_id].find(f => String(f.id) === String(sl.image_file_id))?.image_url || sl.image_url)
                        : sl.image_url;
                    return { src: url || '', alt: sl.title || '' };
                }).filter(sl => sl.src)} />

            <style>{`
                @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </section>
    );
}
